/**
 * Реестр участков — разметка 1:1 из legacy htdocs/core/templates/admin_areas.tpl
 * (таблица собиралась там в JS по ответу /admin/area_list с globarealist: true).
 *
 * Соответствие legacy:
 *   .tableblock.wnowrap                — контейнер таблицы
 *   table.tablesorter                  — реестр (плагин tablesorter + theme.default)
 *   tbody.tablesorter-no-sort          — строка «Итого:» (cssInfoBlock, не сортируется)
 *   tbody.arealist > tr.areaitem       — строки участков, клик → карточка участка
 *   .controlshow (⊞/⊟)                 — раскрытие дочерних строк связанных участков
 *   tr.syncedarea{id}.row-details      — дочерняя строка (cssChildRow), серая, скрыта
 *   .syncedarea (├ / └)                — «дерево» у связанных участков
 *
 * jQuery не подключаем: сортировка сделана на React, но DOM повторяет то, что
 * рисовал tablesorter (.tablesorter-default / th.tablesorter-header / *-headerAsc|Desc,
 * sorter-false у колонок с data-sorter="false") — чтобы theme.default.min.css лёг как раньше.
 * Не перенесён виджет stickyHeaders (прилипающая шапка) — только внешний вид таблицы.
 *
 * API: GET /api/admin/areas?mode=global (Handlers\Admin\Areas::index).
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { toNumber } from '@/lib/format'
import { useApi } from '@/lib/useApi'
import { useStylesheets } from '@/lib/useStylesheets'

/** Строка реестра (legacy area_list + Areas::withMoney). */
interface AreaRow {
  id: number
  name: string
  area_pid: number | null
  money: string
  debtval: string
  mmoneyddebtval: string
  land_area_rate: string
  familia: string | null
  imuy: string | null
  otchestvo: string | null
  candidate: string | null
  rassl_sms: string | null
  rassl_email: string | null
  count_users: number
  count_autoplates: number
  syncid: number | null
  countsync: number
}

/** Строка после разбора связей: маркер дерева и видимость (legacy trhide/stylegrey). */
interface PreparedRow {
  row: AreaRow
  marker: string
  child: boolean
}

type SortDir = 'asc' | 'desc'

/** Колонки — порядок и заголовки как в admin_areas.tpl. */
const COLUMNS: ReadonlyArray<{
  title: string
  css: string
  sortable: boolean
  value?: (row: AreaRow) => string | number
}> = [
  { title: 'Участок', css: 'area', sortable: true, value: (row) => row.name },
  { title: '№ л/с', css: 'area', sortable: true, value: (row) => row.area_pid ?? 0 },
  {
    title: 'Собственник',
    css: 'owner',
    sortable: true,
    value: (row) => fio(row),
  },
  { title: 'Баланс', css: 'balance', sortable: true, value: (row) => toNumber(row.money) },
  { title: 'Квитанции', css: 'debt', sortable: true, value: (row) => toNumber(row.debtval) },
  {
    title: 'Задолженность',
    css: 'debt',
    sortable: true,
    value: (row) => toNumber(row.mmoneyddebtval),
  },
  { title: 'Ставка', css: 'debt', sortable: true, value: (row) => toNumber(row.land_area_rate) },
  { title: 'Доп. Контакты', css: 'area', sortable: true, value: (row) => row.count_users },
  {
    title: 'Номера автомобилей',
    css: 'area',
    sortable: true,
    value: (row) => row.count_autoplates,
  },
  { title: 'Кандидат', css: 'candidate', sortable: false },
  { title: 'Рассылка квитанций', css: 'inform', sortable: false },
]

/** legacy: familia/imuy/otchestvo с подменой null на пустую строку. */
function fio(row: AreaRow): string {
  return `${row.familia ?? ''} ${row.imuy ?? ''} ${row.otchestvo ?? ''}`
}

/** legacy: new Intl.NumberFormat('ru-RU').format(...) в строке «Итого». */
function formatTotal(value: number): string {
  return new Intl.NumberFormat('ru-RU').format(value)
}

function compare(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'ru', { numeric: true })
}

export function AdminAreasPage() {
  // legacy admin_areas.tpl: <link rel="stylesheet" href="/assets/css/theme.default.min.css">
  useStylesheets(['/assets/css/theme.default.min.css'])

  const { data } = useApi<AreaRow[]>('/api/admin/areas?mode=global')
  const navigate = useNavigate()

  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set())
  const [sort, setSort] = useState<{ column: number; dir: SortDir } | null>(null)

  const rows = useMemo<AreaRow[]>(() => data ?? [], [data])

  /** Итоги — по всем строкам ответа, включая дочерние (как legacy). */
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          money: acc.money + toNumber(row.money),
          debt: acc.debt + toNumber(row.debtval),
          kvitdebt: acc.kvitdebt + toNumber(row.mmoneyddebtval),
        }),
        { money: 0, debt: 0, kvitdebt: 0 },
      ),
    [rows],
  )

  /** Маркеры дерева считаются в порядке ответа — как legacy synccountarea. */
  const prepared = useMemo<PreparedRow[]>(() => {
    const fill = new Map<number, number>()
    const list: PreparedRow[] = []

    for (const row of rows) {
      let marker = ''
      if (row.syncid !== null) {
        const left = fill.get(row.syncid)
        marker = left === 1 ? '└' : '├'
        if (left !== undefined) fill.set(row.syncid, left - 1)
      }
      if (row.countsync > 0) fill.set(row.id, row.countsync)
      list.push({ row, marker, child: row.syncid !== null })
    }
    return list
  }, [rows])

  /** Дочерние строки едут за родителем (tablesorter cssChildRow). */
  const visible = useMemo<Array<{ item: PreparedRow; shown: boolean }>>(() => {
    const children = new Map<number, PreparedRow[]>()
    for (const item of prepared) {
      const parentId = item.row.syncid
      if (parentId === null) continue
      children.set(parentId, [...(children.get(parentId) ?? []), item])
    }

    let tops = prepared.filter((item) => !item.child)
    if (sort !== null) {
      const value = COLUMNS[sort.column]?.value
      if (value !== undefined) {
        const sign = sort.dir === 'asc' ? 1 : -1
        tops = [...tops].sort((a, b) => sign * compare(value(a.row), value(b.row)))
      }
    }

    const out: Array<{ item: PreparedRow; shown: boolean }> = []
    const emit = (item: PreparedRow, shown: boolean): void => {
      out.push({ item, shown })
      const kids = children.get(item.row.id) ?? []
      const kidsShown = shown && expanded.has(item.row.id)
      for (const kid of kids) emit(kid, kidsShown)
    }
    for (const top of tops) emit(top, true)
    return out
  }, [prepared, sort, expanded])

  function toggle(areaId: number): void {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(areaId)) next.delete(areaId)
      else next.add(areaId)
      return next
    })
  }

  function sortBy(index: number): void {
    if (!COLUMNS[index].sortable) return
    setSort((prev) =>
      prev === null || prev.column !== index
        ? { column: index, dir: 'asc' }
        : { column: index, dir: prev.dir === 'asc' ? 'desc' : 'asc' },
    )
  }

  /** Классы заголовка — те, что вешал tablesorter (нужны theme.default.min.css). */
  function headerClass(index: number, css: string, sortable: boolean): string {
    if (!sortable) return `${css} tablesorter-header sorter-false`
    if (sort !== null && sort.column === index) {
      return `${css} tablesorter-header ${
        sort.dir === 'asc' ? 'tablesorter-headerAsc' : 'tablesorter-headerDesc'
      }`
    }
    return `${css} tablesorter-header tablesorter-headerUnSorted`
  }

  return (
    <>
      <div className="modalsblock" />

      <div className="tableblock wnowrap">
        <table className="tablesorter tablesorter-default" style={{ width: 'auto' }}>
          <thead>
            <tr className="tablesorter-headerRow">
              {COLUMNS.map((column, index) => (
                <th
                  key={column.title}
                  className={headerClass(index, column.css, column.sortable)}
                  data-sorter={column.sortable ? undefined : 'false'}
                  onClick={() => sortBy(index)}
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="tablesorter-no-sort">
            <tr>
              <td style={{ fontWeight: 'bold' }}>Итого:</td>
              <td />
              <td />
              <td style={{ fontWeight: 'bold', textAlign: 'right' }}>
                {formatTotal(totals.money)} руб.
              </td>
              <td style={{ fontWeight: 'bold', textAlign: 'right' }}>
                {formatTotal(totals.debt)} руб.
              </td>
              <td style={{ fontWeight: 'bold', textAlign: 'right' }}>
                {formatTotal(totals.kvitdebt)} руб.
              </td>
              <td />
              <td />
              <td />
              <td />
              <td />
            </tr>
          </tbody>

          <tbody className="arealist">
            {visible.map(({ item, shown }, index) => {
              const { row } = item
              const classes = ['areaitem']
              if (item.child) classes.push(`syncedarea${row.syncid ?? 0}`, 'row-details')

              return (
                <tr
                  key={`${row.id}-${index}`}
                  className={classes.join(' ')}
                  data-areaid={row.id}
                  style={
                    item.child
                      ? { color: 'grey', display: shown ? undefined : 'none' }
                      : undefined
                  }
                  onClick={() => navigate(`/admin/areas/${row.id}`)}
                >
                  <td className="area">
                    {row.countsync > 0 && (
                      <div
                        className="controlshow"
                        data-areaid={row.id}
                        data-status={expanded.has(row.id) ? 'show' : 'hide'}
                        onClick={(event) => {
                          event.stopPropagation()
                          toggle(row.id)
                        }}
                      >
                        {expanded.has(row.id) ? '⊟' : '⊞'}
                      </div>
                    )}
                    {item.child && <div className="syncedarea">{item.marker}</div>}№{row.name}
                  </td>
                  <td className="area">{row.area_pid}</td>
                  <td className="owner">{fio(row)}</td>
                  <td className="balance">{row.money} руб.</td>
                  <td className="debt">{row.debtval} руб.</td>
                  <td className="debt">{row.mmoneyddebtval} руб.</td>
                  <td className="debt">{row.land_area_rate} руб.</td>
                  <td className="area">{row.count_users}</td>
                  <td className="area">{row.count_autoplates}</td>
                  <td className="candidate">
                    <input type="checkbox" disabled checked={row.candidate === '1'} readOnly />
                  </td>
                  <td className="inform">
                    {/* Индикатор SMS убран: канал отключён. */}
                    <label style={{ marginBottom: 0 }}>
                      Email
                      <input type="checkbox" disabled checked={row.rassl_email === '1'} readOnly />
                    </label>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}
